import { type AuthProvider } from "@refinedev/core";
import { create } from "zustand";
import axios from "axios";
import { User } from "./pages/playground/types";
import { setAuthToken } from "./api";

export const TOKEN_KEY = "refine-auth";

export function completeUser(user: User) {
  return {
    ...user,
    isAdmin: user?.role === "ADMIN",
    isManager: user?.role === "MANAGER",
    isCopywriter: user?.role === "COPYWRITER",
    isClient: user?.role === "CLIENT",
    isCopywriterClient: user?.role === "COPYWRITER_CLIENT",
    isUser: user?.role === "USER",
    isClientRelated:
      user?.role === "CLIENT" || user?.role === "COPYWRITER_CLIENT",
  };
}

export const useAuthStore = create<{
  check: boolean;
  setCheck: (check: boolean) => void;
  user: User | undefined;
  setUser: (user: User | undefined) => void;
}>((set) => ({
  check: false,
  setCheck: (check: boolean) => {
    set({
      check,
    });
  },
  user: undefined,
  setUser: (user: User | undefined) => {
    if (user) {
      set({
        user: completeUser(user),
      });
    } else {
      set({
        user: undefined,
      });
    }
  },
}));

export const authProvider: AuthProvider = {
  login: async ({ email, password }) => {
    const request = new Request(
      `${import.meta.env.VITE_API_URL}/auth/sign-in`,
      {
        method: "POST",
        body: JSON.stringify({ email, password }),
        headers: new Headers({ "Content-Type": "application/json" }),
      }
    );

    try {
      const response = await fetch(request);

      if (response.status < 200 || response.status >= 300) {
        throw new Error(response.statusText);
      }

      const data = (await response.json())?.data;

      if (!data) {
        throw new Error("Invalid response format");
      }

      const { access_token, user } = data;

      localStorage.setItem(TOKEN_KEY, access_token);

      setAuthToken();

      useAuthStore.setState({ user: completeUser(user), check: true });

      return {
        success: true,
      };
    } catch (error) {
      return {
        success: false,
        error: {
          name: "LoginError",
          message: "Credenziali non valide",
        },
      };
    }
  },
  logout: async () => {
    localStorage.removeItem(TOKEN_KEY);

    setAuthToken();

    useAuthStore.setState({ user: undefined, check: true });

    return {
      success: true,
    };
  },
  check: async () => {
    try {
      const token = localStorage.getItem(TOKEN_KEY);

      const res = await axios.get("/auth/current-user", {
        baseURL: import.meta.env.VITE_API_URL,
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      const user = res.data?.data;

      useAuthStore.setState({
        user: completeUser(user),
      });

      return {
        authenticated: true,
      };
    } catch (e) {
      //
    }

    return {
      authenticated: false,
    };
  },
  getPermissions: async () => null,
  getIdentity: async () => {
    const user = useAuthStore.getState().user;

    return user || null;
  },
  onError: async (error) => {
    return { error };
  },
};
